import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon,
  Block as BlockIcon,
  Repeat as RepeatIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addDays, setHours, setMinutes } from 'date-fns';
import providerAppointmentService, { 
  ProviderAppointmentSettingsRequest, 
  ProviderAppointmentSettingsResponse,
  WeeklySchedule,
  DaySchedule as ApiDaySchedule 
} from '../services/providerAppointmentService';

interface TimeSlot {
  id?: number;
  startTime: Date;
  endTime: Date;
  isAvailable: boolean;
}

interface DaySchedule {
  dayOfWeek: number; // 0-6 (Sunday-Saturday)
  isAvailable: boolean;
  slots: TimeSlot[];
}

interface BlockedDate {
  id: number;
  date: string;
  reason?: string;
}

interface CalendarSettings {
  sessionDuration: number; // in minutes
  bufferTime: number; // minutes between appointments
  maxDailyAppointments?: number;
  allowSameDay: boolean;
  advanceBookingDays: number;
  weeklySchedule: DaySchedule[];
  blockedDates: BlockedDate[];
  timezone: string;
}

const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Convert API schedule format to local state format
const convertApiScheduleToLocal = (apiSchedule: WeeklySchedule): DaySchedule[] => {
  return dayKeys.map((dayKey, index) => {
    const apiDay = apiSchedule[dayKey as keyof WeeklySchedule];
    const slots: TimeSlot[] = [];
    
    if (apiDay && apiDay.available && apiDay.startTime && apiDay.endTime) {
      // Parse time strings and create Date objects
      const [startHour, startMin] = apiDay.startTime.split(':').map(Number);
      const [endHour, endMin] = apiDay.endTime.split(':').map(Number);
      
      slots.push({
        startTime: setMinutes(setHours(new Date(), startHour), startMin),
        endTime: setMinutes(setHours(new Date(), endHour), endMin),
        isAvailable: true
      });
      
      // TODO: Handle break times if needed
    }
    
    return {
      dayOfWeek: index,
      isAvailable: apiDay?.available || false,
      slots: slots.length > 0 ? slots : [{
        startTime: setMinutes(setHours(new Date(), 9), 0),
        endTime: setMinutes(setHours(new Date(), 17), 0),
        isAvailable: true
      }]
    };
  });
};

// Convert local state format to API format
const convertLocalScheduleToApi = (localSchedule: DaySchedule[]): WeeklySchedule => {
  const apiSchedule: any = {};
  
  localSchedule.forEach((day, index) => {
    const dayKey = dayKeys[index];
    const apiDay: ApiDaySchedule = {
      available: day.isAvailable
    };
    
    if (day.isAvailable && day.slots.length > 0) {
      // Use the first slot for now (API doesn't support multiple slots yet)
      const firstSlot = day.slots[0];
      apiDay.startTime = format(firstSlot.startTime, 'HH:mm');
      apiDay.endTime = format(firstSlot.endTime, 'HH:mm');
      // TODO: Handle break times
    }
    
    apiSchedule[dayKey] = apiDay;
  });
  
  return apiSchedule as WeeklySchedule;
};

const CalendarSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<CalendarSettings>({
    sessionDuration: 30,
    bufferTime: 10,
    maxDailyAppointments: 8,
    allowSameDay: false,
    advanceBookingDays: 30,
    weeklySchedule: daysOfWeek.map((_, index) => ({
      dayOfWeek: index,
      isAvailable: index >= 1 && index <= 5, // Monday-Friday by default
      slots: [
        {
          startTime: setMinutes(setHours(new Date(), 9), 0),
          endTime: setMinutes(setHours(new Date(), 12), 0),
          isAvailable: true
        },
        {
          startTime: setMinutes(setHours(new Date(), 13), 0),
          endTime: setMinutes(setHours(new Date(), 17), 0),
          isAvailable: true
        }
      ]
    })),
    blockedDates: [],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
  
  const [selectedDay, setSelectedDay] = useState<number>(1); // Monday
  const [blockDateDialog, setBlockDateDialog] = useState(false);
  const [blockDateData, setBlockDateData] = useState({
    date: new Date(),
    reason: ''
  });
  const [editingSlot, setEditingSlot] = useState<{ dayIndex: number; slotIndex: number } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      const apiSettings = await providerAppointmentService.getAppointmentSettings();
      
      // Convert API response to local state format
      const convertedSchedule = convertApiScheduleToLocal(apiSettings.weeklySchedule);
      
      setSettings({
        sessionDuration: apiSettings.slotDuration || 30,
        bufferTime: apiSettings.bufferTime || 10,
        maxDailyAppointments: 8, // Not in API yet
        allowSameDay: apiSettings.minAdvanceBooking === 0,
        advanceBookingDays: apiSettings.maxAdvanceBooking || 30,
        weeklySchedule: convertedSchedule,
        blockedDates: [], // Not in API yet
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load calendar settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      // Convert local state to API format
      const apiSettings: ProviderAppointmentSettingsRequest = {
        slotDuration: settings.sessionDuration,
        bufferTime: settings.bufferTime,
        maxAdvanceBooking: settings.advanceBookingDays,
        minAdvanceBooking: settings.allowSameDay ? 0 : 24,
        autoApprove: false,
        allowStudentCancellation: true,
        cancellationDeadline: 24,
        weeklySchedule: convertLocalScheduleToApi(settings.weeklySchedule)
      };
      
      await providerAppointmentService.updateAppointmentSettings(apiSettings);
      
      toast.success('Calendar settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save calendar settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDayToggle = (dayIndex: number) => {
    const newSchedule = [...settings.weeklySchedule];
    newSchedule[dayIndex].isAvailable = !newSchedule[dayIndex].isAvailable;
    setSettings({ ...settings, weeklySchedule: newSchedule });
  };

  const handleAddSlot = (dayIndex: number) => {
    const newSchedule = [...settings.weeklySchedule];
    const lastSlot = newSchedule[dayIndex].slots[newSchedule[dayIndex].slots.length - 1];
    const newSlot: TimeSlot = {
      startTime: lastSlot ? lastSlot.endTime : setMinutes(setHours(new Date(), 9), 0),
      endTime: lastSlot ? addDays(lastSlot.endTime, 0) : setMinutes(setHours(new Date(), 17), 0),
      isAvailable: true
    };
    newSchedule[dayIndex].slots.push(newSlot);
    setSettings({ ...settings, weeklySchedule: newSchedule });
  };

  const handleDeleteSlot = (dayIndex: number, slotIndex: number) => {
    const newSchedule = [...settings.weeklySchedule];
    newSchedule[dayIndex].slots.splice(slotIndex, 1);
    setSettings({ ...settings, weeklySchedule: newSchedule });
  };

  const handleSlotTimeChange = (dayIndex: number, slotIndex: number, field: 'startTime' | 'endTime', value: Date | null) => {
    if (!value) return;
    
    const newSchedule = [...settings.weeklySchedule];
    newSchedule[dayIndex].slots[slotIndex][field] = value;
    setSettings({ ...settings, weeklySchedule: newSchedule });
  };

  const handleBlockDate = () => {
    const newBlockedDate: BlockedDate = {
      id: Date.now(), // Temporary ID
      date: format(blockDateData.date, 'yyyy-MM-dd'),
      reason: blockDateData.reason
    };
    
    setSettings({
      ...settings,
      blockedDates: [...settings.blockedDates, newBlockedDate]
    });
    
    setBlockDateDialog(false);
    setBlockDateData({ date: new Date(), reason: '' });
    toast.success('Date blocked successfully');
  };

  const handleRemoveBlockedDate = (id: number) => {
    setSettings({
      ...settings,
      blockedDates: settings.blockedDates.filter(date => date.id !== id)
    });
  };

  const copyToAllDays = (dayIndex: number) => {
    const sourceSchedule = settings.weeklySchedule[dayIndex];
    const newSchedule = settings.weeklySchedule.map((day, index) => {
      if (index === dayIndex) return day;
      return {
        ...day,
        slots: sourceSchedule.slots.map(slot => ({ ...slot }))
      };
    });
    setSettings({ ...settings, weeklySchedule: newSchedule });
    toast.success('Schedule copied to all days');
  };

  const renderDaySchedule = (day: DaySchedule, dayIndex: number) => {
    const isSelected = selectedDay === dayIndex;
    
    return (
      <Paper
        key={dayIndex}
        sx={{ 
          p: 2, 
          mb: 2, 
          cursor: 'pointer',
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'divider'
        }}
        onClick={() => setSelectedDay(dayIndex)}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">{daysOfWeek[dayIndex]}</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={day.isAvailable}
                  onChange={() => handleDayToggle(dayIndex)}
                  onClick={(e) => e.stopPropagation()}
                />
              }
              label="Available"
              onClick={(e) => e.stopPropagation()}
            />
            {dayIndex > 0 && dayIndex < 6 && (
              <Tooltip title="Copy to all weekdays">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToAllDays(dayIndex);
                  }}
                >
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        
        {day.isAvailable && (
          <Box>
            {day.slots.map((slot, slotIndex) => (
              <Box 
                key={slotIndex} 
                display="flex" 
                alignItems="center" 
                gap={1} 
                mb={1}
                onClick={(e) => e.stopPropagation()}
              >
                <Chip
                  icon={<TimeIcon />}
                  label={`${format(slot.startTime, 'h:mm a')} - ${format(slot.endTime, 'h:mm a')}`}
                  variant="outlined"
                  color="primary"
                />
                <IconButton
                  size="small"
                  onClick={() => setEditingSlot({ dayIndex, slotIndex })}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                {day.slots.length > 1 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteSlot(dayIndex, slotIndex)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleAddSlot(dayIndex);
              }}
            >
              Add Time Slot
            </Button>
          </Box>
        )}
      </Paper>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/dashboard')}
            size="small"
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Calendar Settings
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSaveSettings}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* General Settings */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                General Settings
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Session Duration (minutes)"
                    value={settings.sessionDuration}
                    onChange={(e) => setSettings({ 
                      ...settings, 
                      sessionDuration: parseInt(e.target.value) || 30 
                    })}
                    InputProps={{ inputProps: { min: 15, max: 120, step: 15 } }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Buffer Time (minutes)"
                    value={settings.bufferTime}
                    onChange={(e) => setSettings({ 
                      ...settings, 
                      bufferTime: parseInt(e.target.value) || 0 
                    })}
                    InputProps={{ inputProps: { min: 0, max: 60, step: 5 } }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Daily Appointments"
                    value={settings.maxDailyAppointments || ''}
                    onChange={(e) => setSettings({ 
                      ...settings, 
                      maxDailyAppointments: parseInt(e.target.value) || undefined 
                    })}
                    InputProps={{ inputProps: { min: 1, max: 50 } }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Advance Booking Days"
                    value={settings.advanceBookingDays}
                    onChange={(e) => setSettings({ 
                      ...settings, 
                      advanceBookingDays: parseInt(e.target.value) || 30 
                    })}
                    InputProps={{ inputProps: { min: 1, max: 365 } }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.allowSameDay}
                        onChange={(e) => setSettings({ 
                          ...settings, 
                          allowSameDay: e.target.checked 
                        })}
                      />
                    }
                    label="Allow same-day appointments"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Blocked Dates */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Blocked Dates
                </Typography>
                <Button
                  size="small"
                  startIcon={<BlockIcon />}
                  onClick={() => setBlockDateDialog(true)}
                >
                  Block Date
                </Button>
              </Box>
              
              {settings.blockedDates.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No dates blocked
                </Typography>
              ) : (
                <List dense>
                  {settings.blockedDates.map((blocked) => (
                    <ListItem key={blocked.id}>
                      <ListItemText
                        primary={format(new Date(blocked.date), 'MMM dd, yyyy')}
                        secondary={blocked.reason}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleRemoveBlockedDate(blocked.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Weekly Schedule */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Weekly Schedule
              </Typography>
              
              {settings.weeklySchedule.map((day, index) => renderDaySchedule(day, index))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Block Date Dialog */}
      <Dialog open={blockDateDialog} onClose={() => setBlockDateDialog(false)}>
        <DialogTitle>Block Date</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date to Block"
                  value={blockDateData.date}
                  onChange={(newValue) => setBlockDateData({ 
                    ...blockDateData, 
                    date: newValue || new Date() 
                  })}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                  minDate={new Date()}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason (Optional)"
                value={blockDateData.reason}
                onChange={(e) => setBlockDateData({ 
                  ...blockDateData, 
                  reason: e.target.value 
                })}
                placeholder="e.g., Holiday, Conference, Personal day"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBlockDate}>
            Block Date
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Time Slot Dialog */}
      {editingSlot && (
        <Dialog 
          open={Boolean(editingSlot)} 
          onClose={() => setEditingSlot(null)}
        >
          <DialogTitle>Edit Time Slot</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label="Start Time"
                    value={settings.weeklySchedule[editingSlot.dayIndex].slots[editingSlot.slotIndex].startTime}
                    onChange={(newValue) => handleSlotTimeChange(
                      editingSlot.dayIndex,
                      editingSlot.slotIndex,
                      'startTime',
                      newValue
                    )}
                    slotProps={{
                      textField: { fullWidth: true }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label="End Time"
                    value={settings.weeklySchedule[editingSlot.dayIndex].slots[editingSlot.slotIndex].endTime}
                    onChange={(newValue) => handleSlotTimeChange(
                      editingSlot.dayIndex,
                      editingSlot.slotIndex,
                      'endTime',
                      newValue
                    )}
                    slotProps={{
                      textField: { fullWidth: true }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingSlot(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default CalendarSettingsPage;